import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  FaCompass,
  FaChessKnight,
  FaHandshake,
  FaGlobeAmericas,
  FaHospital,
  FaUniversity,
  FaShoppingCart,
  FaBuilding,
  FaIndustry,
  FaHotel,
  FaSeedling,
  FaRocket,
  FaFilm,
  FaTruck,
  FaLandmark,
  FaHandsHelping,
  FaUtensils,
  FaBroadcastTower,
  FaBolt,
  FaPiggyBank
} from 'react-icons/fa'
import SEO from '../components/SEO/SEO'
import WhyFigo from '../components/WhyFigo'
import FAQ from '../components/FAQ'
import CTA from './CTA'
import '../styles/pages/AboutPage.css'

const AboutPage = () => {
  const values = [
    {
      icon: <FaCompass />,
      title: 'Innovation First',
      description: 'We leverage cutting-edge technology to deliver transformative solutions'
    },
    {
      icon: <FaChessKnight />,
      title: 'Strategic Thinking',
      description: 'Every solution is designed with your business goals in mind'
    },
    {
      icon: <FaHandshake />,
      title: 'Client Partnership',
      description: 'We work as an extension of your team, not just vendors'
    },
    {
      icon: <FaGlobeAmericas />,
      title: 'Global Reach, Local Expertise',
      description: 'African innovation with world-class delivery standards'
    }
  ]

  const [selectedIndustry, setSelectedIndustry] = useState(null)

  const industries = [
    {
      name: 'Healthcare & Medical',
      icon: <FaHospital />,
      description: 'Digital solutions for hospitals, clinics, and healthcare providers',
      services: ['Patient Management Systems', 'Telemedicine Platforms', 'Health Analytics', 'Medical Records Management']
    },
    {
      name: 'Financial Services',
      icon: <FaPiggyBank />,
      description: 'Secure fintech solutions for banks, insurance, and investment firms',
      services: ['Banking Apps', 'Payment Gateways', 'Trading Platforms', 'Financial Analytics']
    },
    {
      name: 'Education & E-Learning',
      icon: <FaUniversity />,
      description: 'Modern learning platforms for schools, universities, and training centers',
      services: ['LMS Platforms', 'Virtual Classrooms', 'Student Portals', 'Course Management']
    },
    {
      name: 'E-Commerce & Retail',
      icon: <FaShoppingCart />,
      description: 'Scalable online stores and retail management solutions',
      services: ['Online Stores', 'Inventory Systems', 'Point of Sale', 'Multi-vendor Marketplaces']
    },
    {
      name: 'Real Estate & Property',
      icon: <FaBuilding />,
      description: 'Property management and real estate marketplace platforms',
      services: ['Property Listings', 'Virtual Tours', 'Tenant Management', 'CRM Systems']
    },
    {
      name: 'Manufacturing & Industry',
      icon: <FaIndustry />,
      description: 'Industrial automation and supply chain management',
      services: ['Production Tracking', 'Supply Chain', 'Quality Control', 'IoT Integration']
    },
    {
      name: 'Hospitality & Tourism',
      icon: <FaHotel />,
      description: 'Booking systems and guest management for hotels and travel',
      services: ['Booking Platforms', 'Guest Management', 'Tour Planning', 'Review Systems']
    },
    {
      name: 'Agriculture & Agritech',
      icon: <FaSeedling />,
      description: 'Smart farming solutions and agricultural marketplaces',
      services: ['Farm Management', 'Crop Monitoring', 'Agri Marketplaces', 'Supply Chain']
    },
    {
      name: 'Technology & Startups',
      icon: <FaRocket />,
      description: 'MVP development and scaling solutions for tech companies',
      services: ['MVP Development', 'SaaS Platforms', 'API Development', 'Cloud Infrastructure']
    },
    {
      name: 'Media & Entertainment',
      icon: <FaFilm />,
      description: 'Content platforms and streaming solutions',
      services: ['Streaming Platforms', 'Content Management', 'Social Networks', 'Digital Publishing']
    },
    {
      name: 'Logistics & Transportation',
      icon: <FaTruck />,
      description: 'Fleet management and delivery tracking systems',
      services: ['Fleet Tracking', 'Route Optimization', 'Warehouse Management', 'Delivery Apps']
    },
    {
      name: 'Government & Public Sector',
      icon: <FaLandmark />,
      description: 'Digital transformation for government agencies',
      services: ['Citizen Portals', 'Document Management', 'E-Government Services', 'Public Data Systems']
    },
    {
      name: 'Non-Profit & NGOs',
      icon: <FaHandsHelping />,
      description: 'Digital tools for social impact organizations',
      services: ['Donor Management', 'Campaign Platforms', 'Volunteer Coordination', 'Impact Tracking']
    },
    {
      name: 'Food & Beverage',
      icon: <FaUtensils />,
      description: 'Restaurant management and food delivery platforms',
      services: ['Online Ordering', 'Table Reservations', 'Kitchen Management', 'Delivery Systems']
    },
    {
      name: 'Telecommunications',
      icon: <FaBroadcastTower />,
      description: 'Network management and customer service platforms',
      services: ['Customer Portals', 'Billing Systems', 'Network Monitoring', 'Service Management']
    },
    {
      name: 'Energy & Utilities',
      icon: <FaBolt />,
      description: 'Smart grid solutions and utility management',
      services: ['Smart Metering', 'Energy Management', 'Billing Systems', 'Grid Monitoring']
    }
  ]

  // const timeline = [
  //   { year: '2020', event: 'FIGO Digital Services founded in Abuja' },
  //   { year: '2021', event: 'Expanded to fintech and health tech sectors' },
  //   { year: '2022', event: 'Launched AI & Automation division' },
  //   { year: '2023', event: 'Reached ₦100M in project value' },
  //   { year: '2024', event: 'Expanding across Africa and globally' }
  // ]

  return (
    <div className="about-page">
      <SEO
        title="About Us - FIGO Digital Services | Leading Digital Transformation Agency in Nigeria"
        description="Learn about FIGO Digital Services - Nigeria's premier digital transformation agency. We design, build, and scale digital products that help organizations grow faster, operate smarter, and compete globally."
        keywords="about FIGO digital, digital agency Nigeria, software company Abuja, tech company Lagos, African tech innovation, digital transformation experts, custom software developers Nigeria"
        url="https://figodigitalservices.com/about"
      />
      <div className="page-header">
        <motion.h1
          initial={{ y: 20 }}
          animate={{ y: 0 }}
        >
          About FIGO Digital Services
        </motion.h1>
        <motion.p
          initial={{ y: 20 }}
          animate={{ y: 0 }}
          transition={{ delay: 0.1 }}
        >
          We design, build, and scale digital products and intelligent systems
          that help organizations grow faster, operate smarter, and compete globally.
        </motion.p>
      </div>

      <section className="about-mission">
        <div className="about-mission-container">
          <h2>Our Mission</h2>
          <div className="mission-layout">
            <div className="mission-image-wrapper">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&auto=format&fit=crop&q=80"
                alt="Team collaboration"
                className="mission-image"
              />
            </div>
            <div className="mission-card">
              <p>
                To empower businesses worldwide through innovative digital solutions,
                bridging the gap between traditional operations and modern technology,
                and driving digital transformation across Africa and beyond.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="about-values">
        <div className="about-values-container">
          <h2>Our Values</h2>
          <div className="values-grid">
            {values.map((value, index) => (
              <div
                key={index}
                className="value-card"
              >
                <div className="value-icon">
                  {value.icon}
                </div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why FIGO Is Different */}
      <WhyFigo />

      {/* Engagement Models Section */}
      <section className="engagement-models">
        <div className="engagement-models-container">
          <div className="engagement-header">
            <span className="engagement-badge">How We Work</span>
            <h2>Engagement Models</h2>
            <p>Flexible partnership options designed to fit your needs and budget</p>
          </div>

          <div className="engagement-grid">
            {[
              {
                icon: '🚀',
                title: 'One-Off Transformation Projects',
                description: 'Complete digital transformation with a defined scope, timeline, and deliverables. Perfect for organizations ready for a full digital overhaul.',
                features: ['Fixed scope & budget', 'Clear milestones', 'Full documentation', 'Training included']
              },
              {
                icon: '📈',
                title: 'Phased Rollouts',
                description: 'Gradual implementation in manageable phases. Start small, prove value, then scale. Ideal for risk-conscious organizations.',
                features: ['Start with pilot', 'Prove ROI first', 'Scale gradually', 'Minimize disruption']
              },
              {
                icon: '🤝',
                title: 'Monthly Support & Optimization',
                description: 'Ongoing partnership for continuous improvement. We become an extension of your team, handling maintenance and optimization.',
                features: ['Dedicated support', 'Regular optimization', 'Priority response', 'Strategic guidance']
              }
            ].map((model, index) => (
              <div
                key={index}
                className="engagement-card"
              >
                <div className="engagement-icon">{model.icon}</div>
                <h3 className="engagement-title">{model.title}</h3>
                <p className="engagement-description">{model.description}</p>
                <ul className="engagement-features">
                  {model.features.map((feature, idx) => (
                    <li key={idx}>
                      <span className="feature-check">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Work With Section */}
      <section className="about-industries">
        <div className="about-industries-container">
          <div className="industries-header">
            <span className="industries-badge">Industries</span>
            <h2>Industries We Work With</h2>
            <p>We deliver tailored digital solutions across diverse industries worldwide. Click to learn more.</p>
          </div>

          <div className="industries-grid">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="industry-item"
                onClick={() => setSelectedIndustry(industry)}
                style={{ cursor: 'pointer' }}
              >
                <div className="industry-icon">
                  {industry.icon}
                </div>
                <span className="industry-name">{industry.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Modal */}
      <AnimatePresence>
        {selectedIndustry && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedIndustry(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '1rem'
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#ffffff',
                border: '1px solid #E2E8F0',
                borderRadius: '24px',
                padding: '2.5rem',
                maxWidth: '500px',
                width: '100%',
                position: 'relative',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
              }}
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedIndustry(null)}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  border: '1px solid #E2E8F0',
                  background: '#f8fafc',
                  color: 'var(--text-secondary)',
                  fontSize: '1.25rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
                  e.currentTarget.style.color = '#ef4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.borderColor = '#E2E8F0';
                  e.currentTarget.style.color = 'var(--text-secondary)';
                }}
              >
                ×
              </button>

              {/* Modal Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #2563eb 0%, #0d9488 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  color: 'white',
                  boxShadow: '0 4px 10px rgba(37, 99, 235, 0.3)'
                }}>
                  {selectedIndustry.icon}
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: 'var(--text-primary)',
                  margin: 0
                }}>
                  {selectedIndustry.name}
                </h3>
              </div>

              {/* Description */}
              <p style={{
                color: 'var(--text-secondary)',
                fontSize: '1rem',
                lineHeight: '1.7',
                marginBottom: '1.5rem'
              }}>
                {selectedIndustry.description}
              </p>

              {/* Services List */}
              <div>
                <h4 style={{
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: 'var(--figo-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '1rem'
                }}>
                  Key Solutions We Offer
                </h4>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '0.75rem'
                }}>
                  {selectedIndustry.services.map((service, idx) => (
                    <li
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: 'var(--text-primary)',
                        fontSize: '0.9rem'
                      }}
                    >
                      <span style={{
                        color: '#22c55e',
                        fontSize: '1rem'
                      }}>✓</span>
                      {service}
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA Button */}
              <a
                href={`/contact?service=transform`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginTop: '2rem',
                  padding: '1rem 2rem',
                  background: 'linear-gradient(135deg, #2563eb 0%, #0d9488 100%)',
                  borderRadius: '50px',
                  color: 'white',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  textDecoration: 'none',
                  transition: 'transform 0.3s ease, boxShadow 0.3s ease',
                  boxShadow: '0 4px 10px rgba(37, 99, 235, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 12px rgba(37, 99, 235, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 10px rgba(37, 99, 235, 0.3)';
                }}
              >
                Discuss Your {selectedIndustry.name.split(' ')[0]} Project →
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <FAQ />
      <CTA />
    </div>
  )
}

export default AboutPage
